import React from 'react';
import { AppButton, AppEmptyState, AppInput, AppSelect } from '../ui';
import DepotListCard from './DepotListCard';
import { HISTORY_RECENT_LIMIT } from './depotUtils';

/**
 * Panneau historique : toolbar, filtres, liste des dépôts.
 * Les bascules Moi/Équipe et Récents/Tous gardent `depot-scope-switch`
 * (look dépôt intentionnel, distinct d’AppPillSwitch / kkp-planning).
 */
function DepotHistoryPanel({
  showAll,
  canSeeTeam,
  onShowAllChange,
  pendingReviewCount,
  filterQuery,
  onFilterQueryChange,
  historyTab,
  onHistoryTabChange,
  historyExtraFiltersOpen,
  onToggleFilters,
  filterStatut,
  onFilterStatutChange,
  filterType,
  onFilterTypeChange,
  filterCommercial,
  onFilterCommercialChange,
  personnes,
  listLoading,
  listError,
  depots,
  visibleDepots,
  filteredDepots,
  hasActiveFilters,
  onClearFilters,
  onRetryLoad,
  onFocusForm,
  destinations,
  onRelancer,
  onValider,
  onBebedc,
  onDelete,
  relancingId,
  validatingId,
  bebedcId,
  deletingId,
  uploading,
  bebedcPendingIds,
}) {
  return (
    <div className="card depot-history-card">
      <div className="depot-history-toolbar">
        <div className="depot-history-toolbar__titles min-w-0">
          <p className="depot-history-toolbar__title mb-0">
            {showAll ? 'Fichiers envoyés par l’équipe' : 'Vos fichiers'}
          </p>
          {pendingReviewCount > 0 ? (
            <p className="depot-history-toolbar__pending mb-0">
              {pendingReviewCount} à relire
            </p>
          ) : null}
        </div>
        {canSeeTeam ? (
          <div className="depot-scope-switch" role="group" aria-label="Voir mes fichiers ou ceux de l’équipe">
            <button
              type="button"
              className={`depot-scope-switch__btn${!showAll ? ' is-active' : ''}`}
              aria-pressed={!showAll}
              onClick={() => onShowAllChange(false)}
            >
              Moi
            </button>
            <button
              type="button"
              className={`depot-scope-switch__btn${showAll ? ' is-active' : ''}`}
              aria-pressed={showAll}
              title="Voir aussi les fichiers des collègues"
              onClick={() => onShowAllChange(true)}
            >
              Équipe
            </button>
          </div>
        ) : null}
      </div>

      <div className="depot-history-filters">
        <AppInput
          type="search"
          size="sm"
          className="depot-history-filters__search"
          value={filterQuery}
          onChange={(event) => onFilterQueryChange(event.target.value)}
          placeholder="Chercher un client ou un fichier…"
          aria-label="Chercher un client ou un fichier"
        />
        <div className="depot-scope-switch" role="tablist" aria-label="Nombre de fichiers affichés">
          <button
            type="button"
            className={`depot-scope-switch__btn${historyTab === 'recents' ? ' is-active' : ''}`}
            aria-selected={historyTab === 'recents'}
            onClick={() => onHistoryTabChange('recents')}
          >
            Récents
          </button>
          <button
            type="button"
            className={`depot-scope-switch__btn${historyTab === 'tous' ? ' is-active' : ''}`}
            aria-selected={historyTab === 'tous'}
            onClick={() => onHistoryTabChange('tous')}
          >
            Tous
          </button>
        </div>
        <span className="depot-icon-tip depot-icon-tip--end" data-tooltip={historyExtraFiltersOpen ? 'Masquer les filtres' : 'Filtrer'}>
          <AppButton
            type="button"
            size="sm"
            variant={historyExtraFiltersOpen ? 'primary' : 'outline-secondary'}
            icon="mdi:filter-outline"
            iconWidth={18}
            className="depot-icon-btn"
            aria-expanded={historyExtraFiltersOpen}
            aria-label={historyExtraFiltersOpen ? 'Masquer les filtres' : 'Filtrer'}
            onClick={onToggleFilters}
          />
        </span>
        {historyExtraFiltersOpen ? (
          <div className="depot-history-filters__extra">
            <AppSelect
              size="sm"
              value={filterStatut}
              onChange={(event) => onFilterStatutChange(event.target.value)}
              aria-label="Filtrer par statut"
            >
              <option value="">Tous les statuts</option>
              <option value="a_relire">À relire</option>
              <option value="en_cours">En cours</option>
              <option value="termine">Prêts</option>
              <option value="erreur">Échec</option>
            </AppSelect>
            <AppSelect
              size="sm"
              value={filterType}
              onChange={(event) => onFilterTypeChange(event.target.value)}
              aria-label="Filtrer par type"
            >
              <option value="">Tous les types</option>
              <option value="audio_rdv">Audio</option>
              <option value="document">Document</option>
              <option value="notes">Notes</option>
            </AppSelect>
            {personnes.length > 0 ? (
              <AppSelect
                size="sm"
                value={filterCommercial}
                onChange={(event) => onFilterCommercialChange(event.target.value)}
                aria-label="Filtrer par personne"
              >
                <option value="">Toutes les personnes</option>
                {personnes.map((personne) => (
                  <option key={personne.id} value={personne.id}>
                    {personne.nom}
                  </option>
                ))}
              </AppSelect>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="card-body p-0">
        {listLoading ? (
          <AppEmptyState loading message="Chargement…" />
        ) : listError ? (
          <AppEmptyState
            icon="mdi:alert-circle-outline"
            message={listError}
            actions={[{ key: 'retry', label: 'Réessayer', onClick: onRetryLoad }]}
          />
        ) : depots.length === 0 ? (
          <AppEmptyState
            icon="mdi:inbox-outline"
            message={showAll
              ? 'Personne n’a encore envoyé de fichier.'
              : 'Vos envois et rendez-vous apparaîtront ici.'}
            actions={[{
              key: 'add',
              label: 'Envoyer un fichier',
              variant: 'primary',
              icon: 'mdi:plus',
              onClick: onFocusForm,
            }]}
          />
        ) : visibleDepots.length === 0 ? (
          <AppEmptyState
            icon="mdi:filter-off-outline"
            message="Aucun résultat avec ces filtres."
            actions={hasActiveFilters ? [{
              key: 'reset',
              label: 'Effacer les filtres',
              onClick: onClearFilters,
            }] : []}
          />
        ) : (
          <>
            <div className="depot-list">
              {visibleDepots.map((depot) => (
                <DepotListCard
                  key={depot.id}
                  depot={depot}
                  destinations={destinations}
                  onRelancer={onRelancer}
                  onValider={onValider}
                  onBebedc={onBebedc}
                  onDelete={onDelete}
                  relancingId={relancingId}
                  validatingId={validatingId}
                  bebedcId={bebedcId}
                  deletingId={deletingId}
                  uploading={uploading}
                  bebedcPending={bebedcPendingIds.has(depot.id)}
                />
              ))}
            </div>
            {historyTab === 'recents' && filteredDepots.length > HISTORY_RECENT_LIMIT ? (
              <div className="depot-history-more">
                <AppButton
                  type="button"
                  size="sm"
                  variant="outline-primary"
                  onClick={() => onHistoryTabChange('tous')}
                >
                  Voir les {filteredDepots.length} fichiers
                </AppButton>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default DepotHistoryPanel;
